import { call, put, select, takeLatest } from 'redux-saga/effects'
import { getPcrIdSelector } from '../../home/selectors'
import { throwPcrException } from '../../pcr/store/pcr.actions'
import { IPcr } from '../../pcr/store/pcr.types'
import { getServiceIdSelector } from '../../user/selectors'
import { IPatientVitals } from '../store/vitals.types'
import { VitalsConstants } from '../store/vitals.constants'
import { addToTimeline, setShowDateTimeToast } from '../store/vitals.actions'
import { PcrDocsService } from '../../../services/pcrDocs.service'
import { getPatientVitalsSelector } from '../selectors/patientVitals.selector'
import { IAirwayStateList } from '../../procedures/airway/store/airway.types'
import { IAddMedicationAdministeredStateList } from '../../addMedicationAdministered/store/addMedicationAdministered.types'
import { IVentilatorStateList } from '../../procedures/ventilator/store/ventilator.types'
import { ICardiacStateList } from '../../procedures/cardiac/store/cardiac.types'
import { IDrainStateList } from '../../procedures/drain/store/drain.types'
import { IInitiateIvStateList } from '../../procedures/initiateIv/store/initiateIv.types'
import { IHospitalNotifyStateList } from '../../procedures/hospitalNotify/store/hospitalNotify.types'
import { IIntubationStateList } from '../../intubation/store/intubation.types'
import { IOperationsStateList } from '../../procedures/operations/store/operations.types'
import { ILabsStateList } from '../../procedures/labs/store/labs.types'
import { ITitrateStateList } from '../../procedures/titrate/store/titrate.types'
import { IChildbirthOBStateList } from '../../procedures/childbirthOB/store/childbirthOB.types'
import { IExtricationStateList } from '../../procedures/extrication/store/extrication.types'
import { IImmobilizationStateList } from '../../procedures/immobilization/store/immobilization.types'
import { IMedicalConsultStateList } from '../../procedures/medicalConsult/store/medicalConsult.types'
import { IAddPnMedicationStateList } from '../../procedures/pnMedication/store/pnMedication.types'
import { IPnProcedureStateList } from '../../procedures/pnProcedure/store/pnProcedure.types'
import { IRescueStateList } from '../../procedures/rescue/store/rescue.types'
import { IWoundCareStateList } from '../../procedures/woundCare/store/woundCare.types'
import { setActivityLog } from '../../timeline/store/timeline.actions'
import { performWithMutexHOG } from '../../../services/mutexUtility.service'

type ProcedureStateListName =
  | 'medicationAdministeredStateList'
  | 'airwayStateList'
  | 'ventilatorStateList'
  | 'cardiacStateList'
  | 'drainStateList'
  | 'initiateIvStateList'
  | 'hospitalNotifyStateList'
  | 'intubationStateList'
  | 'operationsStateList'
  | 'labsStateList'
  | 'titrateStateList'
  | 'childbirthOBStateList'
  | 'extricationStateList'
  | 'immobilizationStateList'
  | 'medicalConsultStateList'
  | 'medicationAdministeredStateListPNNVs'
  | 'procedureTypeStateListPNNVs'
  | 'rescueStateList'
  | 'woundCareStateList'

const updateProcedureDateAndTime = (
  pcrDoc: IPcr,
  procedureStateListName: ProcedureStateListName,
  patientVitals: IPatientVitals
) => {
  const { date, time, clientEventId } = patientVitals

  const procedureStateList = pcrDoc.patient[procedureStateListName]
  const procedureIndex = procedureStateList.findIndex((procedureState: any) => {
    return procedureState.key === clientEventId?.toString()
  })
  if (procedureIndex === -1) return

  const procedure = procedureStateList[procedureIndex]
  const updatedProcedure = {
    ...procedure,
    date: date,
    time: time,
  }
  procedureStateList[procedureIndex] = updatedProcedure
  return procedureStateList
}

export function* setChildProcedureDateAndTime() {
  const patientVitals: IPatientVitals = yield select(getPatientVitalsSelector)
  const { date, time, vsAction, clientEventId } = patientVitals
  if (!vsAction || !clientEventId) return

  const dateTime = new Date(date || 0)
  if (time) {
    if (time.split(':').length >= 3) dateTime.setSeconds(+time.split(':')[2])
    dateTime.setMinutes(+time.split(':')[1])
    dateTime.setHours(+time.split(':')[0])
  }

  const serviceId: string = yield select(getServiceIdSelector)
  try {
    const pcrDocsService = new PcrDocsService(serviceId)
    const pcrID: string = yield select(getPcrIdSelector)

    if (pcrID) {
      const pcrDoc: IPcr = yield call([pcrDocsService, pcrDocsService.getPCR], pcrID)
      let updatedPatient = pcrDoc.patient

      if (vsAction === 'Airway') {
        const airwayStateList = updateProcedureDateAndTime(pcrDoc, 'airwayStateList', patientVitals)
        if (airwayStateList) {
          updatedPatient = {
            ...updatedPatient,
            airwayStateList: airwayStateList as IAirwayStateList,
          }
        }
      } else if (vsAction === 'Medication') {
        const medicationStateList = updateProcedureDateAndTime(pcrDoc, 'medicationAdministeredStateList', patientVitals)
        if (medicationStateList) {
          updatedPatient = {
            ...updatedPatient,
            medicationAdministeredStateList: medicationStateList as IAddMedicationAdministeredStateList,
          }
        }
      } else if (vsAction === 'Ventilator') {
        const ventilatorStateList = updateProcedureDateAndTime(pcrDoc, 'ventilatorStateList', patientVitals)
        if (ventilatorStateList) {
          updatedPatient = {
            ...updatedPatient,
            ventilatorStateList: ventilatorStateList as IVentilatorStateList,
          }
        }
      } else if (vsAction === 'Cardiac') {
        const cardiacStateList = updateProcedureDateAndTime(pcrDoc, 'cardiacStateList', patientVitals)
        if (cardiacStateList) {
          updatedPatient = {
            ...updatedPatient,
            cardiacStateList: cardiacStateList as ICardiacStateList,
          }
        }
      } else if (vsAction === 'Drain') {
        const drainStateList = updateProcedureDateAndTime(pcrDoc, 'drainStateList', patientVitals)
        if (drainStateList) {
          updatedPatient = {
            ...updatedPatient,
            drainStateList: drainStateList as IDrainStateList,
          }
        }
      } else if (vsAction === 'Initiate IV') {
        const initiateIvStateList = updateProcedureDateAndTime(pcrDoc, 'initiateIvStateList', patientVitals)
        if (initiateIvStateList) {
          updatedPatient = {
            ...updatedPatient,
            initiateIvStateList: initiateIvStateList as IInitiateIvStateList,
          }
        }
      } else if (vsAction === 'Hosp. Notify') {
        const hospitalNotifyStateList = updateProcedureDateAndTime(pcrDoc, 'hospitalNotifyStateList', patientVitals)
        if (hospitalNotifyStateList) {
          updatedPatient = {
            ...updatedPatient,
            hospitalNotifyStateList: hospitalNotifyStateList as IHospitalNotifyStateList,
          }
        }
      } else if (vsAction === 'Intubation') {
        const intubationStateList = updateProcedureDateAndTime(pcrDoc, 'intubationStateList', patientVitals)
        if (intubationStateList) {
          updatedPatient = {
            ...updatedPatient,
            intubationStateList: intubationStateList as IIntubationStateList,
          }
        }
      } else if (vsAction === 'Operations') {
        const operationsStateList = updateProcedureDateAndTime(pcrDoc, 'operationsStateList', patientVitals)
        if (operationsStateList) {
          updatedPatient = {
            ...updatedPatient,
            operationsStateList: operationsStateList as IOperationsStateList,
          }
        }
      } else if (vsAction === 'Labs') {
        const labsStateList = updateProcedureDateAndTime(pcrDoc, 'labsStateList', patientVitals)
        if (labsStateList) {
          updatedPatient = {
            ...updatedPatient,
            labsStateList: labsStateList as ILabsStateList,
          }
        }
      } else if (vsAction === 'Titrate') {
        const titrateStateList = updateProcedureDateAndTime(pcrDoc, 'titrateStateList', patientVitals)
        if (titrateStateList) {
          updatedPatient = {
            ...updatedPatient,
            titrateStateList: titrateStateList as ITitrateStateList,
          }
        }
      } else if (vsAction === 'Childbirth') {
        const childbirthOBStateList = updateProcedureDateAndTime(pcrDoc, 'childbirthOBStateList', patientVitals)
        if (childbirthOBStateList) {
          updatedPatient = {
            ...updatedPatient,
            childbirthOBStateList: childbirthOBStateList as IChildbirthOBStateList,
          }
        }
      } else if (vsAction === 'Extrication') {
        const extricationStateList = updateProcedureDateAndTime(pcrDoc, 'extricationStateList', patientVitals)
        if (extricationStateList) {
          updatedPatient = {
            ...updatedPatient,
            extricationStateList: extricationStateList as IExtricationStateList,
          }
        }
      } else if (vsAction === 'Immobilization') {
        const immobilizationStateList = updateProcedureDateAndTime(pcrDoc, 'immobilizationStateList', patientVitals)
        if (immobilizationStateList) {
          updatedPatient = {
            ...updatedPatient,
            immobilizationStateList: immobilizationStateList as IImmobilizationStateList,
          }
        }
      } else if (vsAction === 'Medical Consult') {
        const medicalConsultStateList = updateProcedureDateAndTime(pcrDoc, 'medicalConsultStateList', patientVitals)
        if (medicalConsultStateList) {
          updatedPatient = {
            ...updatedPatient,
            medicalConsultStateList: medicalConsultStateList as IMedicalConsultStateList,
          }
        }
      } else if (vsAction === 'Pert Neg Med') {
        const pnMedicationStateList = updateProcedureDateAndTime(
          pcrDoc,
          'medicationAdministeredStateListPNNVs',
          patientVitals
        )
        if (pnMedicationStateList) {
          updatedPatient = {
            ...updatedPatient,
            medicationAdministeredStateListPNNVs: pnMedicationStateList as IAddPnMedicationStateList,
          }
        }
      } else if (vsAction === 'Pert Neg Proc') {
        const pnProcedureStateList = updateProcedureDateAndTime(pcrDoc, 'procedureTypeStateListPNNVs', patientVitals)
        if (pnProcedureStateList) {
          updatedPatient = {
            ...updatedPatient,
            procedureTypeStateListPNNVs: pnProcedureStateList as IPnProcedureStateList,
          }
        }
      } else if (vsAction === 'Rescue') {
        const rescueStateList = updateProcedureDateAndTime(pcrDoc, 'rescueStateList', patientVitals)
        if (rescueStateList) {
          updatedPatient = {
            ...updatedPatient,
            rescueStateList: rescueStateList as IRescueStateList,
          }
        }
      } else if (vsAction === 'Wound Care') {
        const woundCareStateList = updateProcedureDateAndTime(pcrDoc, 'woundCareStateList', patientVitals)
        if (woundCareStateList) {
          updatedPatient = {
            ...updatedPatient,
            woundCareStateList: woundCareStateList as IWoundCareStateList,
          }
        }
      }

      const timelineItems = pcrDoc.patient.timelineItems
      if (!timelineItems) return

      const timelineItemIndex = timelineItems.findIndex((item) => {
        return item.key === clientEventId.toString()
      })
      if (timelineItemIndex === -1) return

      const procedureTimelineItem = timelineItems[timelineItemIndex]
      const updatedProcedureTimelineItem = {
        ...procedureTimelineItem,
        date: dateTime,
        labelHeader: time,
        subHeader: date,
      }
      timelineItems[timelineItemIndex] = updatedProcedureTimelineItem
      updatedPatient = {
        ...updatedPatient,
        timelineItems: timelineItems,
      }

      try {
        const pcrDocUpdated = { ...pcrDoc, patient: updatedPatient }
        yield call([pcrDocsService, pcrDocsService.savePCR], pcrDocUpdated)
        yield put(setActivityLog(timelineItems))
        yield put(addToTimeline())
        yield put(setShowDateTimeToast(true))
      } catch (e) {
        yield put(throwPcrException(`savePatientVitals() error: ${e}`))
      }
    } else {
      // todo: add to audit log
    }
  } catch (e) {
    yield put(throwPcrException(`savePatientVitals() error: ${e}`))
  }
}

export function* setChildProcedureDateAndTimeSaga() {
  yield takeLatest([VitalsConstants.SYNC_DATE_TIME], performWithMutexHOG, setChildProcedureDateAndTime)
}
